import { useTranslate } from 'react-admin';
import { MenuItem, Stack, TextField, Typography } from '@mui/material';

import type { Vault } from 'src/pages/admin/services/vaultService';

import { ALL_VAULTS_VALUE, VAULTS_NS as NS } from './constants';
import { KindChip } from './VaultChips';

interface VaultSwitcherProps {
  vaults: Vault[];
  /** The vault in the URL; empty while it is being resolved. */
  value: string;
  /** PRD §14.1: switching vault changes the URL, so the page navigates here rather than holding state. */
  onChange: (vaultId: string) => void;
  disabled?: boolean;
  /** §14.6 Records: offer every vault as one choice, sent as `all` (never the default — the common read stays indexed). */
  allowAll?: boolean;
}

/** Picks one vault for a per-vault page (§14.5 Investors; §14.6 Records imports it too). */
export const VaultSwitcher = ({ vaults, value, onChange, disabled, allowAll }: VaultSwitcherProps) => {
  const translate = useTranslate();
  const known = (allowAll && value === ALL_VAULTS_VALUE) || vaults.some((v) => v.vaultId === value);
  return (
    <TextField
      select
      size="small"
      label={translate(`${NS}.switcher`)}
      value={known ? value : ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || vaults.length === 0}
      sx={{ minWidth: { xs: 1, sm: 320 } }}
    >
      {allowAll && (
        <MenuItem value={ALL_VAULTS_VALUE}>
          <Typography variant="body2">{translate(`${NS}.allVaults`)}</Typography>
        </MenuItem>
      )}
      {vaults.map((v) => (
        <MenuItem key={v.vaultId} value={v.vaultId}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {v.name}
            </Typography>
            <KindChip kind={v.kind} />
            <Typography
              variant="caption"
              noWrap
              sx={{ color: 'text.secondary', fontFamily: 'monospace' }}
            >
              {v.vaultId}
            </Typography>
          </Stack>
        </MenuItem>
      ))}
    </TextField>
  );
};
